/**
 * Page-level header — the shared page-title shell (product pages like
 * Dashboard / Recommendations / Evidence Hypotheses / Experiments Decisions
 * AND the /_eng/* engineering scaffolding pages).
 *
 * Source: BRIEF_ia-redesign-frontend-implementation § 5.1 — the global
 * Header.tsx now carries the IA-REDESIGN chrome (logo + TreeSelect + Mode
 * badge + Bell + Avatar). This `PageHeader` shim retains the prior
 * `<Header title=... description=... actions=... />` API without forcing
 * pages to re-implement the page-title pattern. Carries the BRANDING.md
 * § 6.1 hairline signature (Track D, 2026-08-01) so every consumer reads
 * the same website design language.
 */

interface PageHeaderProps {
  title: string;
  description?: string;
  actions?: React.ReactNode;
}

export default function PageHeader({
  title,
  description,
  actions,
}: PageHeaderProps) {
  return (
    <header className="flex items-center justify-between gap-4 border-b border-divider bg-surface px-6 py-4">
      <div className="min-w-0">
        {/* BRANDING.md § 6.1 hairline signature above the page title. */}
        <div className="mb-2 h-px w-12 bg-primary" aria-hidden="true" />
        <h1 className="text-xl font-bold text-ink">{title}</h1>
        {description && (
          <p className="mt-0.5 text-sm text-muted">{description}</p>
        )}
      </div>
      {actions && (
        <div className="flex shrink-0 items-center gap-3">{actions}</div>
      )}
    </header>
  );
}
